import { select, put, takeEvery, all, takeLatest } from 'redux-saga/effects';
import {
  setTransientValuation,
  setTransientValuationInputs,
  setTransientValuationNoBidReason,
  setTransientValuationType,
  setTransientValuationNotes,
  setValuationIsDirty,
  setShouldCalculateProforma,
  setTransientValuationAfterTaxRefresh,
} from 'store/actions';
import { transientValuationSelector, shouldCalculateProformaSelector, selectedProformaInputsContainerSelector, userSelector } from 'store/selectors';
import { isEqual } from 'lodash'

export function* setTransientValuationSaga() {
  // NOTE: Updating transient valuation is done by reducer, here we just check to update dirty flag(s)
  yield takeEvery(setTransientValuation, function* ({ payload }) {

    const { transientValuation, resetInputDirtyFlags = true } = payload;
    // Prep work
    const [user, selectedProformaInputsContainer] = yield all([select(userSelector), select(selectedProformaInputsContainerSelector)]);
    const userPersistedValuation = selectedProformaInputsContainer?.valuations.find(val => val.user.id === user.id);

    // Need to reset shouldCalculateProforma flag when setTransientValuationSaga() is not triggered from valuation input change, namely not from setTransientValuationInputs
    // One of the uses cases is when calculation result comes back from backend we need to reset need shouldCalculateProforma flag to remove the gray overlay over proforma output section
    if (resetInputDirtyFlags) {
      yield all([put(setShouldCalculateProforma(false))]);
    }

    // Compare new transientValuation with userPersistedValuation to see if there is any difference changes - (changes include valuation input, output, comps selection or simply noBidReason)
    // One of the use cases is when calcuation result comes back from backend we need to check if we need to enable (with differences) or disable (no differences) 'Save' button
    if (userPersistedValuation) {
      const valuationIsDirty = transientValuation != null && !isEqual(transientValuation, userPersistedValuation);  // compare changes include all sections: input, output, nobidreason, notes
      yield put(setValuationIsDirty(valuationIsDirty));
    }
  });

  /** This is a pipeline method in between valuation input changes and updating transient valuation.
  Its main purpose is to update shouldCalculateProforma flag */
  yield takeLatest(setTransientValuationInputs, function* ({ payload }) {
    const { valuationInput, skipCalculate } = payload;
    // Prep work
    const [transientValuation, shouldCalculateProforma] = yield all([select(transientValuationSelector), select(shouldCalculateProformaSelector)]);

    if (!skipCalculate && shouldCalculateProforma === false) {
      yield put(setShouldCalculateProforma(true))
    }

    // Continue to update transientValuation
    const updatedTransientValuation = { ...transientValuation, valuationInput }
    yield put(setTransientValuation(updatedTransientValuation, false)); // false is to tell the calling method to not reset shouldCalculateProforma flag
  });

  /**This is a pipeline method in between valuation noBidReason change and updating transient valuation.
  Its main purpose is to not let setTransientValuation reset shouldCalculateProforma flag */
  yield takeLatest(setTransientValuationNoBidReason, function* ({ payload: noBidReason }) {
    // Prep work
    const transientValuation = yield select(transientValuationSelector);

    // Continue to update transientValuation
    const updatedTransientValuation = { ...transientValuation, noBidReason }
    yield put(setTransientValuation(updatedTransientValuation, false)); // false is to tell the calling method to not reset shouldCalculateProforma flag
  });

  yield takeLatest(setTransientValuationType, function* ({ payload: valuationType }) {
    const transientValuation = yield select(transientValuationSelector);
    const updatedTransientValuation = { ...transientValuation, type: valuationType }
    yield put(setTransientValuation(updatedTransientValuation, false)); // false is to tell the calling method to not reset shouldCalculateProforma flag
  });

  /**This is a pipeline method in between valuation notes change and updating transient valuation.
  Its main purpose is to not let setTransientValuation reset shouldCalculateProforma flag */
  yield takeLatest(setTransientValuationNotes, function* ({ payload: notes }) {
    // Prep work
    const transientValuation = yield select(transientValuationSelector);

    // Continue to update transientValuation
    const updatedTransientValuation = { ...transientValuation, notes }
    yield put(setTransientValuation(updatedTransientValuation, false)); // false is to tell the calling method to not reset shouldCalculateProforma flag
  });

  /**This is a pipeline method in between valuation tax refresh and updating transient valuation. */
  yield takeLatest(setTransientValuationAfterTaxRefresh, function* () {
    // Get latest transientValuation
    const transientValuation = yield select(transientValuationSelector);

    // reset transientValuation tax inputs
    let newValuationInput = { ...transientValuation.valuationInput };
    newValuationInput['taxes'] = null;
    newValuationInput['taxesType'] = null;

    yield put(setTransientValuationInputs(newValuationInput)); 
  });
}